import { Fragment, useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { Dialog, Transition } from '@headlessui/react';
import { add, eachDayOfInterval, endOfMonth, format, getDay, isEqual, isSameDay, isSameMonth, isToday, parse, parseISO, startOfToday } from 'date-fns';
import CalendarSkeleton from '../../../../components/appointmentCalendar/CalendarSkeleton';
import es from 'date-fns/locale/es';
import { Appointment } from '../../../services/Appointment';
import { useInfoStore } from '../../../../App';
import { useNavigate } from 'react-router-dom';
import Today from '../../../components/Today';
import configurations from '../../../../common/utils/configurations';
import CustomSuccessModal from '../../../../common/components/customSuccessModal/CustomSuccessModal';
import CustomRedAlert from '../../../../common/components/customRedAlert/CustomRedAlert';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

let colStartClasses = ['', 'col-start-2', 'col-start-3', 'col-start-4', 'col-start-5', 'col-start-6', 'col-start-7'];

const AppointmentPending = ({ isHomeRoute }) => {
  const { userInfo } = useInfoStore((state) => state);
  let today = startOfToday();
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [calcelledSchedules, setCancelledSchedules] = useState([]);
 
  let [selectedDay, setSelectedDay] = useState(today);
  let [currentMonth, setCurrentMonth] = useState(format(today, 'MMM-yyyy'));
  let firstDayCurrentMonth = parse(currentMonth, 'MMM-yyyy', new Date());

  const [showDetail, setShowDetail] = useState(false);
  const [patient, setPatient] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorBody, setErrorBody] = useState('');
  const [deleteInstructions, setDeleteInstructions] = useState('');
  const [deleteReasonError, setDeleteReasonError] = useState('');

  const fetchAppointments = () => {
    setLoading(true);
    Appointment.getAppointment()
      .then((res) => {
        setSchedules(res.data.pending);
        setCancelledSchedules(res.data.blockSlots);
      })
      .catch((e) => console.log(e))
      .finally(() => {
        setLoading(false);
        setHasLoadedOnce(true);
      });
  };
  
  useEffect(() => {
    fetchAppointments();
  }, [selectedDay]);
  
  
  if (isLoading && !hasLoadedOnce) {
    return <CalendarSkeleton />;
  }

  let days = eachDayOfInterval({ start: firstDayCurrentMonth, end: endOfMonth(firstDayCurrentMonth) });

  function previousMonth() {
    let firstDayNextMonth = add(firstDayCurrentMonth, { months: -1 });
    setCurrentMonth(format(firstDayNextMonth, 'MMM-yyyy'));
  }

  function nextMonth() {
    let firstDayNextMonth = add(firstDayCurrentMonth, { months: 1 });
    setCurrentMonth(format(firstDayNextMonth, 'MMM-yyyy'));
  }

  const fxItem = (item) => {
   
    setShowDetail(true);
    setPatient(item);
  };

  const handleDeleteClick = () => {
    if (!patient?.apptID) {
      setShowErrorModal(true);
      setErrorBody('Seleccione una cita antes de eliminarla.');
      return;
    }
    setDeleteInstructions('');
    setDeleteReasonError('');
    setOpenDeleteModal(true);
  };

  const handleDeleteAppointment = async () => {
    if (!deleteInstructions.trim()) {
      setDeleteReasonError('Debe ingresar un motivo de eliminación.');
      return;
    }

    try {
      await Appointment.deleteAppointment({
        idAppointment: patient.apptID,
        deleteInstructions: deleteInstructions.trim()
      });
      setOpenDeleteModal(false);
      setShowDetail(false);
      setPatient({});
      setDeleteInstructions('');
      setDeleteReasonError('');
      fetchAppointments();
      setShowSuccessModal(true);
    } catch (error) {
      console.log(error);
      setOpenDeleteModal(false);
      setDeleteInstructions('');
      setDeleteReasonError('');
      setShowErrorModal(true);
      setErrorBody('No se pudo eliminar la cita. Inténtelo de nuevo.');
    }
  };

  const checkActiveSlot = (item) => {
    
    if (item.apptID && item.apptID === patient.apptID) {
      return true;
    } else if (item.idappt_without_user && item.idappt_without_user === patient.idappt_without_user) {
      return true;
    }
    return false;
  };
  let selectedDayAppointments = schedules.filter((appointment) => isSameDay(parseISO(`${appointment.appDate}T${appointment.appTime}`), selectedDay));
   
  return (
    <div className="space-y-6 sm:px-6 lg:px-0 lg:col-span-10 side-bar-right bg-dl-screen-lightblue">
    <div className="">
      <Today onDelete={handleDeleteClick} deleteDisabled={!patient?.apptID} />
      <div className="calender-box box-radious mt-12">
        <div className="md:grid md:grid-cols-2">
          <div className="bg-white me-5 py-7 px-7 rounded-lg">
            <div className="flex items-center calender-top-head">
              <h2 className="flex-auto font-bold text-gray-900 capitalize calender-title mb-0">
                {format(firstDayCurrentMonth, 'MMMM yyyy', { locale: es })}
              </h2>
              <button
                type="button"
                onClick={previousMonth}
                className="-my-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Previous month</span>
                <ChevronLeftIcon className="w-5 h-5" aria-hidden="true" />
              </button>
              <button
                onClick={nextMonth}
                type="button"
                className="-my-1.5 -mr-1.5 ml-2 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Next month</span>
                <ChevronRightIcon className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            <div className="grid grid-cols-7 mt-10 text-xl leading-6 text-center text-gray-500 week-name-list">
              {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((item, i) => (
                <div key={i}>{item}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 mt-2 text-xl">
              {days.map((day, dayIdx) => (
                <div
                  key={day.toString()}
                  className={classNames(dayIdx === 0 && colStartClasses[getDay(day)], 'py-1.5')}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedDay(day);
                      setShowDetail(false);
                    }}
                    className={classNames(
                      isEqual(day, selectedDay) && 'text-white',
                      !isEqual(day, selectedDay) && isToday(day) && 'text-dl-red',
                      !isEqual(day, selectedDay) &&
                        !isToday(day) &&
                        isSameMonth(day, firstDayCurrentMonth) &&
                        'text-gray-900',
                      !isEqual(day, selectedDay) &&
                        !isToday(day) &&
                        !isSameMonth(day, firstDayCurrentMonth) &&
                        'text-gray-400',
                      isEqual(day, selectedDay) && isToday(day) && 'bg-dl-red',
                      isEqual(day, selectedDay) && !isToday(day) && 'bg-dl-accent',
                      !isEqual(day, selectedDay) && 'hover:bg-gray-200',
                      (isEqual(day, selectedDay) || isToday(day)) && 'font-semibold',
                      day < today && 'cursor-not-allowed text-gray-300 text-lg',
                      'mx-auto flex h-8 w-8 items-center justify-center rounded-full active-btn-number'
                    )}
                  >
                    <time dateTime={format(day, 'yyyy-MM-dd')}>{format(day, 'd')}</time>
                  </button>

                  {/* ✅ Red dot logic fixed */}
                  <div className="w-2 h-2 mx-auto mt-1">
                    {schedules.some((appointment) =>
                      isSameDay(parseISO(`${appointment.appDate}T${appointment.appTime}`), day)
                    ) && <div className="w-2 h-2 rounded-full bg-sky-500"></div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <section className="mt-12 md:mt-0 md:pl-14 border-l border-black ml-8">
            <ul className="time-slot-name-text">
              {selectedDayAppointments.length ? (
                selectedDayAppointments.map((item, i) => (
                  <li key={i} onClick={() => fxItem(item)} className={checkActiveSlot(item) ? 'active' : ''}>
                    <span>{item.userFullName}</span>
                    <p>{item.appTime}</p>
                  </li>
                ))
              ) : (
                <p className="no-time-show">Seleccione una hora</p>
              )}
             
            </ul>
          </section>
        </div>
      </div>

      {showDetail && (
        <div className="flex information-box-info common-box-white mt-10 ">
          <div className="name-docter-info-box-L flex">
            <figure>
              <span>
                <img className="h-full w-full rounded-full" src={configurations?.defaultImage} alt="" />
              </span>
            </figure>
            <figcaption className="information-box-info-left">
              <h4 className="title-info">{patient.userFullName}</h4>
              <span className="blue-tag">{patient.appTime}</span>
            </figcaption>
          </div>
          <hr className="border-right"></hr>
          <div className="name-docter-info-box-C">
            <h4 className="title-info">Detalles:</h4>
            <ul className="Detalles-list">
              <li className="mb-2">
                <p>fecha de nacimiento: {patient.birthDate}</p>
              </li>
              <li>
                <p>Motivo de la consulta: {patient.motiveDescription}</p>
              </li>
            </ul>
          </div>
          <div className="w-1/5 flex flex-col gap-2 items-center">
            <button
              className="btn btn-primary mb-3"
              onClick={() => navigate('/doctor-appointment-detail', { state: { patient, type: 'viewOnly' } })}
            >
              Ver detalles
            </button>
            <button
              className="btn btn-primary grey-btn"
              onClick={() => navigate('/doctor-appointment-detail', { state: { patient, type: 'reschedule' } })}
            >
              Reprogramar
            </button>
          </div>
        </div>
      )}

      {!isHomeRoute &&
        userInfo?.profileType === 'withAppointment' && (
          <div className="flex justify-center mt-20">
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/selected-appointment-motive-doctor')}>
              Agendar nueva cita
            </button>
          </div>
        )}
      {openDeleteModal && (
        <Transition.Root show={openDeleteModal} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={() => setOpenDeleteModal(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                        {'¿Quieres eliminar esta cita?'}
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">Ingrese el motivo por el cual desea eliminar la cita.</p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Motivo</label>
                      <textarea
                        rows={4}
                        value={deleteInstructions}
                        onChange={(e) => {
                          setDeleteInstructions(e.target.value);
                          if (deleteReasonError) {
                            setDeleteReasonError('');
                          }
                        }}
                        className="block w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:border-dl-primary-500 focus:outline-none focus:ring-1 focus:ring-dl-primary-500"
                        placeholder="Escriba el motivo de eliminación"
                      />
                      {deleteReasonError ? (
                        <p className="mt-2 text-sm text-red-700">{deleteReasonError}</p>
                      ) : null}
                    </div>

                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                        onClick={handleDeleteAppointment}
                      >
                        Eliminar
                      </button>
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                        onClick={() => {
                          setOpenDeleteModal(false);
                          setDeleteInstructions('');
                          setDeleteReasonError('');
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      )}
      {showSuccessModal && (
        <CustomSuccessModal
          open={showSuccessModal}
          title={'Cita eliminada exitosamente'}
          buttonText={'Aceptar'}
          buttonAction={() => setShowSuccessModal(false)}
        />
      )}
      {showErrorModal && (
        <CustomRedAlert
          open={showErrorModal}
          title={'Ocurrió un error.'}
          bodyText={errorBody}
          onClose={() => {
            setShowErrorModal(false);
            setErrorBody('');
          }}
        />
      )}
    </div>
  </div>
  );
};

export default AppointmentPending;
